import { Box, Theme, Typography } from "@outschool/backpack";
import {
  SavedClassListsForParentProfileQuery,
  SavedClassListsForParentProfileQueryVariables,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import {
  addParamsToUrl,
  createUrlWithActivity,
  searchPath,
} from "@outschool/routes";
import { gql, useQuery } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { useIsMobile, useLinkComponent } from "@outschool/ui-utils";
import React, { useMemo } from "react";

import * as GraduatingHeartPNG from "../../../../images/graduating-heart.png";
import ListingCarousel from "../../listings/carousels/ListingCarousel";
import { EmptyProfileCard } from "../EmptyProfileCard";
import { Give20Get20ClassShare } from "../Give20Get20";
import { PrivacyDisclaimerText } from "../PrivacyDisclaimerText";

const SavedListsQuery = gql`
  query SavedClassListsForParentProfile(
    $uid: ID!
    $profileLinkUid: String
    $publicView: Boolean
    $skipRating: Boolean!
    $withRecentReviews: Boolean!
  ) {
    savedClassListsForParentProfile(
      userUid: $uid
      profileLinkUid: $profileLinkUid
      publicView: $publicView
    ) {
      uid
      name
      privacyStatus
      listings(searchFilters: {}, limit: 15) {
        ...ListingCarousel_listing
      }
    }
  }
  ${ListingCarousel.fragments.listing}
`;

export function MyFavoritesCarousel({
  parentUid,
  isCurrentUsersProfile,
  profileLinkUid,
  privacyState,
  privateLink,
  publicView,
}: {
  parentUid: string;
  isCurrentUsersProfile: boolean;
  profileLinkUid?: string;
  privacyState?: string | null;
  privateLink: string;
  publicView: boolean;
}) {
  const { t } = useTranslation("client\\routes\\ParentProfile");
  const { data, error, loading } = useQuery<
    SavedClassListsForParentProfileQuery,
    SavedClassListsForParentProfileQueryVariables
  >(SavedListsQuery, {
    variables: {
      uid: parentUid,
      profileLinkUid,
      publicView,
      skipRating: true,
      withRecentReviews: false,
    },
  });

  if (
    loading ||
    !data ||
    (!isCurrentUsersProfile && !loading && !data) ||
    (!isCurrentUsersProfile &&
      !loading &&
      !data?.savedClassListsForParentProfile) ||
    (!isCurrentUsersProfile &&
      !loading &&
      !data?.savedClassListsForParentProfile.length) ||
    error
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: "64px",
      }}
    >
      <Box
        sx={(theme: Theme) => ({
          mx: "1em",
          mb: "1rem",
          [theme.breakpoints.down("sm")]: { mx: 0 },
        })}
      >
        <Typography emphasized variant={"h6"}>
          {t("My Family’s Favorite Classes")}
        </Typography>
        {isCurrentUsersProfile && (
          <PrivacyDisclaimerText
            privacyState={privacyState}
            privateLink={privateLink}
          />
        )}
      </Box>
      {data.savedClassListsForParentProfile.map(list => (
        <FavouritesCarousel
          list={list}
          key={`favourites-list-${list.uid}`}
          isCurrentUsersProfile={isCurrentUsersProfile}
        />
      ))}
      {!data.savedClassListsForParentProfile.length &&
        isCurrentUsersProfile && (
          <EmptyProfileCard
            imageUrl={GraduatingHeartPNG}
            topText={t("You don’t have any favorites yet!")}
            bottomText={t("Start building your class wishlist!")}
            buttonText={t("Start adding favorites")}
            buttonTo={searchPath()}
          />
        )}
    </Box>
  );
}

export function FavouritesCarousel({
  list,
  isCurrentUsersProfile,
}: {
  list: NonNullable<
    SavedClassListsForParentProfileQuery["savedClassListsForParentProfile"]
  >[0];
  isCurrentUsersProfile: boolean;
}) {
  const { t } = useTranslation("client\\routes\\ParentProfile");
  const { currentUser } = useSession();
  const isMobile = useIsMobile();

  const Link = useLinkComponent();

  const [showShareModalAtIndex, setShowShareModalAtIndex] = React.useState<
    null | number
  >(null);

  const listings = list.listings;

  const { activity, activityWithLeader } =
    useMemo(() => {
      if (showShareModalAtIndex === null) {
        return;
      }

      if (!listings) {
        return;
      }

      const activity = listings[showShareModalAtIndex]?.activity;

      if (!activity) {
        return;
      }

      const activityWithLeader = !listings[showShareModalAtIndex]?.leader
        ? activity
        : {
            ...activity,
            leader: listings[showShareModalAtIndex]?.leader,
          };

      return { activity, activityWithLeader };
    }, [listings, showShareModalAtIndex]) || {};

  const shareLink = useMemo(() => {
    if (!activity) {
      return;
    }

    return addParamsToUrl(createUrlWithActivity(activity), {
      signup: true,
      usid: currentUser?.slug_id,
    });
  }, [currentUser, activity]);

  const onClose = useMemo(() => {
    return () => setShowShareModalAtIndex(null);
  }, [setShowShareModalAtIndex]);

  if (!listings) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <ListingCarousel
        shareButtonFunction={
          isCurrentUsersProfile
            ? index => setShowShareModalAtIndex(index)
            : undefined
        }
        // @ts-expect-error this is fine
        listings={listings}
        trackingLabel="explore"
        carouselRowNum={1}
        isCrossSell={true}
        leftAlign
        sx={{
          paddingLeft: 0,
          paddingRight: 0,
          marginLeft: 0,
          marginRight: 0,
          maxWidth: "100%",
          paddingTop: 0,
          paddingBottom: 0,
          padding: 0,
          ...(isMobile
            ? {
                paddingTop: 0,
                paddingBottom: 0,
              }
            : {}),
        }}
        innerContainerSx={{
          padding: 0,
          paddingTop: "1em",
          paddingLeft: 0,
          maxWidth: undefined,
          justifyContent: "flex-start",
        }}
        titleElement={
          <Box
            flex
            sx={{
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <Typography emphasized variant="h6">
              {list.name}
            </Typography>
            {(isCurrentUsersProfile || list.privacyStatus === "unlisted") && (
              <Link to={`/saved-classes/${list.uid}`}>
                <Typography
                  sx={(theme: Theme) => ({
                    mr: "2em",
                    [theme.breakpoints.down("lg")]: {
                      paddingRight: 0,
                    },
                  })}
                >
                  {t("See all")}
                </Typography>
              </Link>
            )}
          </Box>
        }
      />
      <Give20Get20ClassShare
        shareLink={shareLink}
        onClose={onClose}
        usesOutReachReferral={
          !!currentUser?.teacher_approved_at && !currentUser?.suspended_at
        }
        activity={activityWithLeader}
      />
    </Box>
  );
}
